import { useSearchParams } from 'react-router-dom'
import { Download, Loader2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FiscalYearPicker } from '@/components/sales-trackers/FiscalYearPicker'
import { NoBillingsYet } from '@/components/sales-trackers/no-billings-yet'
import { isNoBillingsYet } from '@/components/sales-trackers/billings-empty'
import {
  useBillingsFiscalYears,
  useBillingsMyFiscalYears,
  useBillingsRepRoster,
  useBillingsTransactions,
} from '@/hooks/use-api'
import { useBillingsPeriod, periodQuery, periodLabel } from '@/hooks/use-billings-period'
import { useDebounce } from '@/hooks/use-debounce'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api'
import type { BillingsTransaction } from '@/types'
import { explainError } from '@/lib/explain-error'
import { formatDate } from '@/lib/utils'
import { exportToCSV } from '@/utils/csvExport'
import { useState } from 'react'

/**
 * The individual invoice lines behind the selected period.
 *
 * WHY THIS PAGE EXISTS. Everything else in Sales Trackers is an aggregate. The only
 * line-level view was a rep's own sheet, which needs you to know whose lines you are
 * looking for, caps at 500 with no paging, and tells you to "export the CSV for the full
 * set" when that export covered only the 500 already loaded.
 *
 * READ-ONLY, DELIBERATELY. Reattribution stays on the rep sheet, where the dialog can say
 * how many identical lines one decision will move. That warning is a correlated count per
 * row; computing it for a whole period to power an action this page does not offer would be
 * paying for nothing.
 *
 * THE TOTAL IS THE POINT. A drill-down that cannot be checked against the figure it came
 * from is just a table. `sum` covers the filtered set, not the page, so it reconciles to the
 * matrix above it.
 */

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'software', label: 'Software' },
  { value: 'remediation', label: 'Remediation' },
  { value: 'other_services', label: 'Other services' },
  { value: 'unclassified', label: 'Unclassified' },
]

const HOUSE = [
  { value: '', label: 'House and non-house' },
  { value: 'only', label: 'House accounts only' },
  { value: 'exclude', label: 'Exclude house accounts' },
]

const SELECT_CLASS = 'mt-1 block h-9 rounded-md border border-input bg-background px-3 text-sm'

/**
 * The backend's ceiling for one request. The export PAGES THROUGH at this size rather than
 * asking once and hoping.
 *
 * The first version asked once for 10,000 and labelled the button "Export all 12,400". The
 * server capped it, the file held 10,000 rows, and nothing anywhere said so - which is the
 * same silent truncation this page exists to replace, rebuilt with a label that explicitly
 * promises the number it does not deliver.
 */
const EXPORT_PAGE_SIZE = 10000

/**
 * A stop, so a runaway loop cannot hang the browser on a pathological filter. Chosen well
 * above the largest real dataset: the whole billings table is about 35,000 lines across
 * every year. Reaching it is reported rather than passed off as a complete file.
 */
const EXPORT_MAX_ROWS = 200000

export default function SalesTrackersTransactions({
  selfService = false,
}: {
  /** A rep looking at their own lines. Uses the session-keyed endpoint, which takes no id. */
  selfService?: boolean
}) {
  useDocumentTitle(selfService ? 'My transactions' : 'Transactions')

  const rows = useOverflowFocus('Individual transactions')
  const { toast } = useToast()
  const [isExporting, setIsExporting] = useState(false)

  const adminYears = useBillingsFiscalYears(!selfService)
  const myYears = useBillingsMyFiscalYears(selfService)
  const years = selfService ? myYears : adminYears

  // 'p' is cleared whenever the period moves: page 5 of September is not page 5 of a
  // shorter August, and staying there shows an empty table under a summary saying otherwise.
  const period = useBillingsPeriod(years.data, ['p'])
  const { fy, granularity, month, quarter, setPeriod } = period

  /*
   * Paging and filters live in the URL beside the period, not in useState.
   *
   * `usePagination` holds the page in state, which is right for a list page that owns its
   * own route. It is wrong here for the reason the period hook's header sets out: these
   * routes remount, and a filtered view that silently resets to page one of everything is
   * the failure this whole section already learned about once.
   */
  const [params, setParams] = useSearchParams()
  const page = Math.max(1, Number(params.get('p')) || 1)
  const search = params.get('q') ?? ''
  const rep = params.get('rep') ?? ''
  const category = params.get('category') ?? ''
  // URL-only, set by the drill-down from By Product. It has no select of its own because
  // the groups come from seed data rather than a fixed list; it shows as a chip instead.
  const productGroup = params.get('product_group') ?? ''
  const house = (params.get('house') ?? '') as '' | 'only' | 'exclude'

  const setFilter = (key: string, value: string) => {
    const p = new URLSearchParams(params)
    if (value) p.set(key, value)
    else p.delete(key)
    // Any change to what is being asked for returns to page one. Staying on page 7 of a
    // narrower result shows an empty table and reads as "no transactions".
    if (key !== 'p') p.delete('p')
    setParams(p, { replace: true })
  }

  // 300ms, matching the other list pages: one request per pause, not per keystroke.
  const debouncedSearch = useDebounce(search, 300)

  const roster = useBillingsRepRoster(fy, false, !selfService)

  /*
   * Hoisted so the export replays exactly what is on screen.
   *
   * This is the shape the Opportunities export fix introduced. Eight list pages in this app
   * still export only the rows already loaded, which silently gives somebody 25 rows of a
   * 1,200-row period and no indication that is what happened.
   */
  const listQuery = {
    fy,
    ...periodQuery(period),
    search: debouncedSearch || undefined,
    rep: rep || undefined,
    category: category || undefined,
    product_group: productGroup || undefined,
    house: house || undefined,
  }

  const txns = useBillingsTransactions({ ...listQuery, page, limit: 50 }, selfService)

  const shownPeriod = periodLabel(granularity, {
    fy: fy ?? txns.data?.fiscal_year,
    month,
    quarter,
    monthLabel: txns.data?.months?.[0]?.label,
  })

  const money = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 0 })
  const total = txns.data?.total ?? 0
  const limit = txns.data?.limit ?? 50
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const fetchPage = (p: number) =>
        selfService
          ? apiClient.getBillingsMyTransactions({
              ...listQuery,
              page: p,
              limit: EXPORT_PAGE_SIZE,
            })
          : apiClient.getBillingsTransactions({
              ...listQuery,
              page: p,
              limit: EXPORT_PAGE_SIZE,
            })

      const rows: BillingsTransaction[] = []
      let pageNo = 1
      let expected = 0
      // Keep asking until the server has given back everything it counted. One request
      // covers any realistic period; the loop is what makes the button's promise true when
      // it does not.
      for (;;) {
        const chunk = await fetchPage(pageNo)
        expected = chunk.total
        rows.push(...chunk.data)
        if (
          rows.length >= chunk.total ||
          chunk.data.length === 0 ||
          rows.length >= EXPORT_MAX_ROWS
        ) {
          break
        }
        pageNo += 1
      }

      const complete = rows.length >= expected
      const all = { data: rows }

      exportToCSV({
        filename: `transactions-${shownPeriod.replace(/[^\w-]+/g, '-')}.csv`,
        // Exact values, not the rounded figures shown on screen.
        data: all.data.map((l) => ({
          Date: l.txn_date,
          Customer: l.customer_name ?? '',
          'Product / service': l.product_service ?? '',
          'Sales account': l.sales_account ?? '',
          Invoice: l.doc_number ?? '',
          Rep: l.rep_label ?? 'Unattributed',
          House: l.is_house ? 'Yes' : 'No',
          Category: l.category,
          Amount: l.amount,
        })),
      })
      toast({
        title: complete ? 'Export ready' : 'Export is incomplete',
        description: complete
          ? `${rows.length.toLocaleString()} transactions for ${shownPeriod}.`
          : // Said plainly rather than handing over a short file that looks whole.
            `${rows.length.toLocaleString()} of ${expected.toLocaleString()} transactions. ` +
            `Narrow the period or the filters to export the rest.`,
        variant: complete ? undefined : 'destructive',
      })
    } catch (err) {
      toast({
        title: 'Export failed',
        description: explainError(err, 'Could not fetch the transactions for export.'),
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
            {selfService ? 'My transactions' : 'Transactions'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every invoice line behind {shownPeriod}. Net billings from QuickBooks, in CAD.
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={isExporting || total === 0}>
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Export all {total > 0 ? total.toLocaleString() : ''}
        </Button>
      </div>

      {years.data && years.data.length > 0 && (
        <FiscalYearPicker
          years={years.data}
          value={fy ?? txns.data?.fiscal_year}
          onChange={(next) => setPeriod({ fy: next })}
          period={{ granularity, month, quarter, onChange: setPeriod }}
        />
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[16rem] flex-1">
          <Label htmlFor="txn-search">Search</Label>
          <Input
            id="txn-search"
            className="mt-1"
            placeholder="Customer, product or invoice number"
            value={search}
            onChange={(e) => setFilter('q', e.target.value)}
          />
        </div>

        {!selfService && (
          <div>
            <Label htmlFor="txn-rep">Rep</Label>
            <select
              id="txn-rep"
              className={`${SELECT_CLASS} min-w-[12rem]`}
              value={rep}
              onChange={(e) => setFilter('rep', e.target.value)}
            >
              <option value="">All reps</option>
              {(roster.data ?? []).map((r) => (
                // A person is addressed by id, a bucket by its label - the same
                // distinction the rep sheet draws, so a link between them lands.
                <option key={r.rep_label} value={r.rep_user_id ?? r.rep_label}>
                  {r.display}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <Label htmlFor="txn-category">Category</Label>
          <select
            id="txn-category"
            className={`${SELECT_CLASS} min-w-[11rem]`}
            value={category}
            onChange={(e) => setFilter('category', e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="txn-house">House accounts</Label>
          <select
            id="txn-house"
            className={`${SELECT_CLASS} min-w-[13rem]`}
            value={house}
            onChange={(e) => setFilter('house', e.target.value)}
          >
            {HOUSE.map((h) => (
              <option key={h.value} value={h.value}>
                {h.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {productGroup && (
        <p className="text-sm">
          Filtered to <strong>{productGroup}</strong>.{' '}
          <Button
            variant="link"
            className="h-auto p-0 align-baseline"
            onClick={() => setFilter('product_group', '')}
          >
            Show every product
          </Button>
        </p>
      )}

      <p role="status" aria-live="polite" className="sr-only">
        {txns.isFetching
          ? 'Loading transactions'
          : txns.data
            ? `${txns.data.total} transactions, totalling ${money(txns.data.sum)}`
            : ''}
      </p>

      {txns.isError &&
        (isNoBillingsYet(txns.error) ? (
          <NoBillingsYet what="These transactions" />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not load the transactions</AlertTitle>
            <AlertDescription>
              {explainError(txns.error, 'Something went wrong loading the transactions.')}
            </AlertDescription>
          </Alert>
        ))}

      {txns.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {txns.data && (
        <Card>
          <CardContent className="pt-6">
            {/* The figure that makes this a drill-down rather than a table: it covers the
                whole filtered period, not the page on screen. */}
            <p className="text-sm text-muted-foreground mb-4">
              <strong className="text-foreground">{total.toLocaleString()}</strong>{' '}
              {total === 1 ? 'transaction' : 'transactions'}, totalling{' '}
              <strong className="text-foreground">{money(txns.data.sum)}</strong> for {shownPeriod}.
            </p>

            {total === 0 ? (
              <p className="text-sm text-muted-foreground">
                No transactions match these filters in {shownPeriod}.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto" ref={rows.ref} {...rows.regionProps}>
                  <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <caption className="sr-only">Individual transactions for {shownPeriod}</caption>
                    <thead>
                      <tr className="border-b">
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Date
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Customer
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Product / service
                        </th>
                        <th scope="col" className="text-left font-medium py-2 pr-3">
                          Invoice
                        </th>
                        {!selfService && (
                          <th scope="col" className="text-left font-medium py-2 pr-3">
                            Rep
                          </th>
                        )}
                        <th scope="col" className="text-right font-medium py-2 pl-3">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {txns.data.data.map((l) => (
                        // Keyed on id. line_hash identifies CONTENT and is deliberately
                        // shared by identical lines, so it cannot key a list.
                        <tr key={l.id} className="border-b last:border-0">
                          <td className="py-1.5 pr-3 whitespace-nowrap">
                            {formatDate(l.txn_date)}
                          </td>
                          <th scope="row" className="text-left font-normal py-1.5 pr-3">
                            {l.customer_name ?? '—'}
                            {/* In words, not colour alone. The space is a real text node:
                                a margin separates it visually but the accessible name
                                still read "Acme Corphouse". */}
                            {l.is_house && (
                              <span className="ml-1.5 text-xs text-muted-foreground"> (house)</span>
                            )}
                          </th>
                          <td className="py-1.5 pr-3">{l.product_service ?? '—'}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{l.doc_number ?? '—'}</td>
                          {!selfService && (
                            <td className="py-1.5 pr-3">
                              {l.rep_label ?? (
                                <span className="text-muted-foreground">Unattributed</span>
                              )}
                            </td>
                          )}
                          <td
                            className={`text-right py-1.5 pl-3 ${l.amount < 0 ? 'text-destructive' : ''}`}
                          >
                            {money(l.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between gap-4 flex-wrap pt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
                      {total.toLocaleString()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilter('p', String(page - 1))}
                        disabled={page <= 1}
                        aria-label="Go to the previous page of transactions"
                      >
                        Previous
                      </Button>
                      <span className="text-sm">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilter('p', String(page + 1))}
                        disabled={page >= totalPages}
                        aria-label="Go to the next page of transactions"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  )
}
